import React, { useEffect, useRef, useState } from "react";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import Polygon from "ol/geom/Polygon";
import { fromLonLat, toLonLat } from "ol/proj";
import { Style, Icon, Fill, Stroke } from "ol/style";
import Draw from "ol/interaction/Draw";
import Translate from "ol/interaction/Translate";
import { defaults as defaultInteractions } from "ol/interaction";

const CityAdminMap = ({ 
  centerLat, centerLng, 
  polygonPoints, 
  onCenterChange, onPolygonChange 
}) => {
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  
  // Sources to hold our drawn shapes and the draggable marker
  const polygonSourceRef = useRef(new VectorSource());
  const markerSourceRef = useRef(new VectorSource());
  const drawInteractionRef = useRef(null);

  // If we already have a polygon from the database, start in "Finished" mode
  const [isDrawing, setIsDrawing] = useState(!polygonPoints || polygonPoints.length === 0);

  // Helper to load existing polygon points into OpenLayers format
  const loadExistingPolygon = (points) => {
    polygonSourceRef.current.clear();
    if (points && points.length >= 3) {
      // OpenLayers requires the first and last point to be identical to close the loop
      const closedPoints = [...points, points[0]];
      const projectedCoords = closedPoints.map(p => fromLonLat([p[0], p[1]]));
      
      const feature = new Feature({
        geometry: new Polygon([projectedCoords])
      });
      polygonSourceRef.current.addFeature(feature);
    }
  };

  useEffect(() => {
    if (!mapContainerRef.current) return;

    const lng = Number(centerLng || 51.391173);
    const lat = Number(centerLat || 35.700954);
    const centerCoords = fromLonLat([lng, lat]);

    // 1. Setup the Polygon Layer (Green border with slight fill)
    const polygonLayer = new VectorLayer({
      source: polygonSourceRef.current,
      style: new Style({
        fill: new Fill({ color: "rgba(37, 211, 102, 0.3)" }),
        stroke: new Stroke({ color: "#25D366", width: 2 }),
      }),
    });

    // 2. Setup the Center Marker Layer (Red SVG Pin)
    const pinSVG = '<svg width="32" height="48" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C5.373 0 0 5.373 0 12c0 7.75 12 24 12 24s12-16.25 12-24C24 5.373 18.627 0 12 0zm0 17.5c-3.038 0-5.5-2.462-5.5-5.5S8.962 6.5 12 6.5s5.5 2.462 5.5 5.5-2.462 5.5-5.5 5.5z" fill="#dc3545"/></svg>';
    markerSourceRef.current.addFeature(new Feature(new Point(centerCoords)));
    
    const markerLayer = new VectorLayer({
      source: markerSourceRef.current,
      zIndex: 100, // Keep marker on top of the polygon
      style: new Style({
        image: new Icon({
          src: 'data:image/svg+xml;utf8,' + encodeURIComponent(pinSVG),
          anchor: [0.5, 1],
          scale: 0.8,
        }),
      }),
    });

    // 3. Initialize the Map
    const map = new Map({
      target: mapContainerRef.current,
      layers: [new TileLayer({ source: new OSM() }), polygonLayer, markerLayer],
      view: new View({ center: centerCoords, zoom: 11 }),
      // CRITICAL: Disable doubleClickZoom so we can use double-click to finish drawing the polygon
      interactions: defaultInteractions({ doubleClickZoom: false }),
    });

    // 4. Translate Interaction: Makes ONLY the red marker draggable
    const translate = new Translate({ layers: [markerLayer] });
    map.addInteraction(translate);
    
    translate.on('translateend', (e) => {
      const feature = e.features.getArray()[0];
      const coords = toLonLat(feature.getGeometry().getCoordinates());
      onCenterChange(coords[1], coords[0]); // Return lat, lng
    });

    // 5. Draw Interaction: Let the admin trace the city limits
    const draw = new Draw({
      source: polygonSourceRef.current,
      type: "Polygon",
    });

    draw.on('drawend', (e) => {
      // When the admin double-clicks, the polygon is finished
      setIsDrawing(false);
      map.removeInteraction(draw);

      // Extract the coordinates, un-project them to standard GPS, and remove the duplicate closing point for your backend
      const rawCoords = e.feature.getGeometry().getCoordinates()[0];
      
      // OpenLayers includes the closing point automatically, we slice it off to match your original [lng, lat] distinct points logic
      const distinctPoints = rawCoords.slice(0, -1).map(c => toLonLat(c));
      
      onPolygonChange(distinctPoints);
    });

    drawInteractionRef.current = draw;
    mapInstanceRef.current = map;

    // Load initial data if editing an existing city
    loadExistingPolygon(polygonPoints);
    if (!polygonPoints || polygonPoints.length === 0) {
      map.addInteraction(draw);
    }

    return () => map.setTarget(null);
  }, []);

  // Handle the Clear Button
  const handleClearPolygon = () => {
    polygonSourceRef.current.clear();
    onPolygonChange([]);
    setIsDrawing(true);
    
    // Re-attach the drawing tool to the map
    if (mapInstanceRef.current && drawInteractionRef.current) {
      mapInstanceRef.current.addInteraction(drawInteractionRef.current);
    }
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-header bg-white d-flex flex-column flex-md-row justify-content-between align-items-md-center p-3">
        <div>
          <span className="small text-muted d-block mb-1">
            برای رسم مرز کلیک کنید و در انتها <b>دوبار کلیک (Double-Click)</b> کنید تا مرز بسته شود.
          </span>
          {isDrawing ? (
            <span className="badge bg-warning text-dark"><i className="fa fa-pen mr-1"/> در حال رسم...</span>
          ) : (
            <span className="badge bg-success"><i className="fa fa-check mr-1"/> رسم مرز تکمیل شد</span>
          )}
        </div>
        
        <button 
          type="button" 
          className="btn btn-sm btn-outline-danger rounded-pill px-3 mt-2 mt-md-0" 
          onClick={handleClearPolygon}
          disabled={isDrawing && polygonSourceRef.current?.getFeatures().length === 0}
        >
          <i className="fa fa-trash mr-2" />
          پاک کردن مرزها
        </button>
      </div>
      <div 
        ref={mapContainerRef} 
        style={{ width: "100%", height: "400px", position: "relative" }} 
      />
    </div>
  );
};

export default CityAdminMap;